import algoliasearch from 'algoliasearch/lite'
import { graphql, HeadProps, PageProps, navigate } from 'gatsby'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Tab,
  TabPanel,
  Tabs,
  TabsBody,
  TabsHeader,
} from '@material-tailwind/react/components/Tabs'
import { ListBulletIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { AppHead, Layout } from '../../components/common'
import {
  Search,
  SearchStatus,
  SearchStatusPropsValue,
} from '../../components/search'
import { SearchSection } from '../../components/page/offers/list/SearchSection'
import { OfferList } from '../../components/page/offers/list/OfferList'
import { OfferListMap } from '../../components/page/offers/list/OfferListMap'
import { SearchResultNb } from '../../components/page/offers/list/SearchStatus'
import { getDefaultActiveTab } from '../../Utils/functionnals'

const Offres: React.FC<PageProps<Queries.OfferPageQuery>> = ({
  data,
  location,
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    getDefaultActiveTab(location) || 'list',
  )
  const [isMobileMenuOpen, setIisMobileMenuOpen] = useState(false)
  const [searchStatus, setSearchStatus] = useState<SearchStatusPropsValue>()

  const siteStrapiUrl = data.site?.siteMetadata?.siteStrapiUrl || ''
  const googleMapsApiKey = data.site?.siteMetadata?.googleMaps?.apiKey || ''

  const indexName = data.site?.siteMetadata?.algolia?.offersIndex || ''
  const appId = data.site?.siteMetadata?.algolia?.appId || ''
  const searchKey = data.site?.siteMetadata?.algolia?.searchkey || ''
  const searchClient = useMemo(() => algoliasearch(appId, searchKey), [])

  const tabsData = [
    {
      label: 'Liste',
      key: 'list',
      icon: ListBulletIcon,
      component: (
        <OfferList
          siteStrapiUrl={siteStrapiUrl}
          isResearchSubscriptionButtonActive={!isMobileMenuOpen}
        />
      ),
    },
    {
      label: 'Carte',
      key: 'map',
      icon: MapPinIcon,
      component: (
        <OfferListMap
          googleApiKey={googleMapsApiKey}
          list={searchStatus?.results || []}
          totalItems={searchStatus?.nbResults || 0}
        />
      ),
    },
  ]

  const searchParams = new URLSearchParams(location.search)

  const isMapTab = searchParams?.get('tab') === 'map'

  useEffect(() => {
    setIisMobileMenuOpen(isMapTab)

    const crispScript = document.getElementById('crisp-chatbox')
    if (isMapTab) {
      if (crispScript) {
        crispScript.setAttribute('data-hidden', 'true')
      }
    } else if (crispScript) {
      crispScript.setAttribute('data-hidden', 'false')
    }
  }, [location.search])

  return (
    <Layout
      careerSite={false}
      hideFooter={isMapTab}
      apiToken={data.site?.siteMetadata?.frontStrapiToken || undefined}
      apiUrl={data.site?.siteMetadata?.siteStrapiUrl || undefined}
      location={location}
    >
      <div
        id="page-content-container"
        className="h-full mb-2 md:px-8 bg-very-light-blue"
      >
        <Search algolia={{ searchClient, indexName }}>
          <SearchStatus onChange={(val) => setSearchStatus(val)} />
          <div className="h-full mx-auto space-y-4 bg-very-light-blue lg:w-[1115px] 2xl:w-[1300px]">
            <SearchSection
              searchClient={searchClient}
              googleMapsApiKey={googleMapsApiKey}
            />
            <Tabs value={activeTab}>
              <TabsHeader
                className="sticky top-0 left-0 flex flex-col p-0 px-4 space-y-4 overflow-hidden bg-transparent rounded-none md:static md:px-0"
                indicatorProps={{
                  className: 'bg-transparent shadow-none rounded-none',
                }}
              >
                <div className="flex items-center space-x-4">
                  {tabsData.map(({ label, key, icon }) => (
                    <Tab
                      key={key}
                      value={key}
                      onClick={() => {
                        setActiveTab(key)
                        searchParams.set('tab', key)
                        navigate(`?${searchParams.toString()}`)
                      }}
                      className="p-0 w-fit"
                    >
                      <span
                        className={`flex py-2 items-center gap-2 text-night ${activeTab === key
                          ? 'text-night border-b-2 border-night'
                          : ''
                          }`}
                      >
                        {React.createElement(icon, { className: 'w-5 h-5' })}
                        <span>{label}</span>
                      </span>
                    </Tab>
                  ))}
                </div>
                <SearchResultNb searchStatus={searchStatus} type="offers" />
              </TabsHeader>
              <TabsBody
                animate={{
                  initial: { y: 250 },
                  mount: { y: 0 },
                  unmount: { y: 250 },
                }}
              >
                {tabsData.map(({ key, component }) => (
                  <TabPanel
                    key={key}
                    value={key}
                    className="p-0 px-4 mt-2 md:px-0"
                  >
                    {!searchStatus?.isLoading && component}
                  </TabPanel>
                ))}
              </TabsBody>
            </Tabs>
          </div>
        </Search>
      </div>
    </Layout>
  )
}

export default Offres

export const Head: React.FC<HeadProps<Queries.OfferPageQuery>> = ({ data }) => {
  const totalCount = data.allStrapiAccountingOffer?.totalCount ?? '900'
  const linkCanonical = data.site?.siteMetadata?.siteUrl
    ? `${data.site?.siteMetadata?.siteUrl}/offres`
    : 'https://lamacompta.co/offres'
  return (
    <AppHead
      title="Offres d'emploi en Cabinet d'Expertise Comptable - Lamacompta"
      algoliaAppId={data.site?.siteMetadata?.algolia?.appId}
      metaDescription={`Découvrez les dernières offres d'emploi en CDI, CDD, stage et alternance sur lamacompta, ${totalCount} offres disponibles maintenant !`}
      metaTitle="Offres Emploi en Cabinet Expertise Comptable"
      linkCanonical={linkCanonical}
      metaImage="https://lamacompta.co/lamacompta-sharing-default.jpg"
    />
  )
}

export const query = graphql`
  query OfferPage {
    site {
      siteMetadata {
        siteUrl
        siteStrapiUrl
        frontStrapiToken
        algolia {
          appId
          searchkey
          offersIndex
        }
        googleMaps {
          apiKey
        }
      }
    }
    allStrapiAccountingOffer {
      totalCount
    }
  }
`
